// Meta CAPI tracking-signal capture for the ActiveCampaign "tracking" field.
//
// PROBLEM this solves: Meta attributes a conversion to the originating ad
// primarily via `fbc` (the ad-click id, derived from the `fbclid` URL param)
// and `fbp` (the browser id). The browser Pixel carries these automatically —
// but when the Pixel is blocked (ad blockers, Safari/iOS ITP, consent not
// granted, script load failure) the ONLY event Meta receives is the server-
// side CAPI Lead fired from the AC webhook (functions/index.js). Without fbc,
// that server event has no click id → Meta cannot tie it to the ad/campaign →
// the conversion is effectively lost for reporting and optimization.
//
// FIX: capture fbc/fbp/user-agent/source-url on /webinar, serialise them as a
// single JSON string, and write it into the AC "tracking" hidden field
// (field[3] — see `window.cfields = {"2":"leadsource","3":"tracking"}` in the
// Form 7 embed). AC stores it on the contact and posts it in the webhook; the
// Cloud Function parses the JSON and forwards the signals into user_data.
//
// KEY INSIGHT: `fbc` can be reconstructed from the `fbclid` URL param even when
// the Pixel never loaded — exactly the ad-blocked case where we need the server
// event to carry attribution. Meta's fbc format:
//   fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
// subdomainIndex is 1 for a root-domain cookie (app-academy.ai).

import { getStoredUtm } from './tracking.js';

const STORAGE_KEY = 'aa_fb_tracking';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 d — matches Meta's click window.

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function readFbclidFromUrl() {
  if (typeof window === 'undefined' || !window.location.search) return null;
  try {
    return new URLSearchParams(window.location.search).get('fbclid');
  } catch {
    return null;
  }
}

// fbc per Meta spec: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
function buildFbc(fbclid, tsMs) {
  return `fb.1.${tsMs}.${fbclid}`;
}

function readStored() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

// Called once at app boot (main.jsx). Persists the fbclid + a reconstructed
// fbc so the value survives to when the registration modal opens AND across
// page reloads / the cross-origin round trip through activehosted.com. The
// capture timestamp is kept stable so the reconstructed fbc doesn't drift.
export function captureFbTracking() {
  if (typeof window === 'undefined') return;
  try {
    const existing = readStored();
    const fbclid =
      readFbclidFromUrl() ||
      (existing && existing.fbclid) ||
      (getStoredUtm() && getStoredUtm().captured && getStoredUtm().captured.fbclid) ||
      null;
    if (!fbclid && !existing) return; // nothing worth persisting yet
    const ts = (existing && existing.ts) || Date.now();
    const payload = {
      ts,
      fbclid: fbclid || null,
      // Reconstructed fbc — used only when the Pixel's _fbc cookie is absent
      // at fill time (i.e. the Pixel never loaded).
      fbcReconstructed: fbclid ? buildFbc(fbclid, ts) : null,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* localStorage disabled (private mode / quota) — silent. */
  }
}

// Build the tracking payload with the freshest data available at call time.
// Prefers the live _fbc/_fbp cookies (set by the Pixel if it loaded), falling
// back to the reconstructed fbc for the ad-blocked case.
export function getFbTrackingPayload() {
  const stored = readStored();
  const fbcCookie = readCookie('_fbc');
  const fbpCookie = readCookie('_fbp');

  const fbc = fbcCookie || (stored && stored.fbcReconstructed) || null;
  const fbp = fbpCookie || null;

  const payload = {};
  if (fbc) payload.fbc = fbc;
  if (fbp) payload.fbp = fbp;
  if (typeof navigator !== 'undefined' && navigator.userAgent) payload.ua = navigator.userAgent;
  if (typeof window !== 'undefined' && window.location) {
    // event_source_url — origin + path only (no query/hash) to keep it clean.
    payload.url = window.location.origin + window.location.pathname;
  }
  return payload;
}

// Conservative cap for the serialised JSON. The AC "tracking" field is a
// hidden single-line text field, which in ActiveCampaign is stored as a
// length-capped column (single-line "Text Input" fields cap at 255 chars).
// If the value ever exceeds the column limit, AC truncates it mid-string and
// the JSON becomes unparseable. We keep the value well under 255 by BUDGETING:
// the critical signals (fbc, then fbp) are added first and always fit; url and
// user-agent only get whatever budget remains (user-agent is truncated). This
// guarantees the field always holds VALID JSON and never loses fbc/fbp.
const MAX_FIELD_LEN = 240;

export function getFbTrackingJson() {
  const p = getFbTrackingPayload();
  const obj = {};
  const fits = (candidate) => {
    try { return JSON.stringify(candidate).length <= MAX_FIELD_LEN; }
    catch { return false; }
  };

  // 1. fbc — highest priority (carries the ad-click id → campaign attribution).
  if (p.fbc && fits({ ...obj, fbc: p.fbc })) obj.fbc = p.fbc;
  // 2. fbp — browser id (improves match quality).
  if (p.fbp && fits({ ...obj, fbp: p.fbp })) obj.fbp = p.fbp;
  // 3. url — short; server has a fallback so it's fine if it ever doesn't fit.
  if (p.url && fits({ ...obj, url: p.url })) obj.url = p.url;
  // 4. ua — truncate to whatever budget remains. Meta tolerates a partial UA,
  //    and the server has no way to recover the browser UA otherwise.
  if (p.ua) {
    let ua = p.ua;
    while (ua.length > 0 && !fits({ ...obj, ua })) ua = ua.slice(0, -4);
    if (ua.length > 0) obj.ua = ua;
  }

  if (Object.keys(obj).length === 0) return '';
  try {
    return JSON.stringify(obj);
  } catch {
    return '';
  }
}

// Attach the filler to the AC form inside `root`. Mirrors attachLeadSourceFiller
// (lead-source.js): a MutationObserver waits for the AC embed script to render
// the form, then writes the tracking JSON into the `fieldName` hidden input.
//
// Two write points, so we capture the freshest signals possible:
//   1. On render — guarantees a value even if the submit hook misses.
//   2. On submit (capture phase) — the Pixel may have set _fbp/_fbc AFTER the
//      initial render (CTA click grants consent + loads the Pixel), so we
//      recompute and overwrite the field right before AC serialises the form.
export function attachFbTrackingFiller(root, fieldName) {
  if (!root || !fieldName || typeof window === 'undefined') return () => {};

  let observer = null;
  let timeoutId = null;
  let hookedForm = null;
  let warnedMissing = false;

  const writeField = () => {
    const input = root.querySelector(`input[name="${fieldName}"]`);
    if (!input) {
      if (import.meta.env.DEV && !warnedMissing) {
        warnedMissing = true;
        const names = Array.from(root.querySelectorAll('input[type="hidden"]'))
          .map((i) => i.name)
          .filter(Boolean);
        // eslint-disable-next-line no-console
        console.warn(
          `[fb-tracking] Hidden field "${fieldName}" not found in rendered AC form. ` +
            `Check AC_TRACKING_FIELD in WebinarPage.jsx. Available hidden fields:`,
          names,
        );
      }
      return false;
    }
    const json = getFbTrackingJson();
    if (json) {
      input.value = json;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.log('[fb-tracking] filled', fieldName, json);
      }
    }
    return true;
  };

  const onSubmit = () => {
    // Do NOT preventDefault — AC's own submit logic must run normally.
    writeField();
  };

  const hookForm = () => {
    if (hookedForm) return;
    const form = root.querySelector('form');
    if (!form) return;
    hookedForm = form;
    form.addEventListener('submit', onSubmit, { capture: true });
  };

  const tryFill = () => {
    // Wait for AC to render the form — no hidden inputs yet means the embed
    // script hasn't injected the form HTML.
    if (root.querySelectorAll('input[type="hidden"]').length === 0) return false;
    const found = writeField();
    hookForm();
    return found;
  };

  // Initial attempt — form may already be rendered if the modal reopens.
  if (tryFill()) {
    return () => {
      if (hookedForm) hookedForm.removeEventListener('submit', onSubmit, { capture: true });
    };
  }

  observer = new MutationObserver(() => {
    if (tryFill() && observer) {
      observer.disconnect();
      observer = null;
    }
  });
  observer.observe(root, { childList: true, subtree: true });

  // Safety net: stop watching after 10 s. AC's auto-scan is usually < 1 s.
  timeoutId = setTimeout(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }, 10000);

  return () => {
    if (observer) observer.disconnect();
    if (timeoutId) clearTimeout(timeoutId);
    if (hookedForm) hookedForm.removeEventListener('submit', onSubmit, { capture: true });
  };
}
